import { getLocation, push, RouterLocation } from "connected-react-router";
import { startOfMonth, startOfQuarter } from "date-fns";
import { uniq } from "lodash-es";
import { defineMessage } from "react-intl";
import { Action } from "redux";
import { all, call, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared/enum";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { StageSummary } from "@mapmycustomers/shared/types/entity/deals/StageSummary";
import Organization from "@mapmycustomers/shared/types/Organization";
import {
  FilterModel,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import { callApi } from "@app/store/api/callApi";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { deleteFunnel, ensureStagesFetched, updateStages } from "@app/store/deal/actions";
import { getEntityTypesWithExternalPreview, getEntityViewSource } from "@app/store/entityView";
import { showEntityView } from "@app/store/entityView/actions";
import { EntityViewSource } from "@app/store/entityView/EntityViewPayload";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getOrganization, getOrganizationId } from "@app/store/iam";
import { getSelectedFilter } from "@app/store/savedFilters";
import { initializeSavedFiltersForView } from "@app/store/savedFilters/actions";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import ListResponseAggregation from "@app/types/viewModel/ListResponseAggregation";
import { MAX_AGGREGATION_LIMIT } from "@app/util/consts";
import dealFieldModel, { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import {
  appendFilterCondition,
  createEmptyFilterModel,
  createOrCondition,
  createSimpleCondition,
  extractSimpleConditionForField,
  getSimpleConditionForField,
  getSimpleConditionIndexForField,
  removeSimpleConditionAtIndex,
  removeSimpleConditionForFields,
  updateSimpleConditionAtIndex,
  upsertSimpleConditions,
} from "@app/util/filters/utils";
import { parseApiDateWithUtcTz } from "@app/util/parsers";
import { isSimpleCondition } from "@app/util/viewModel/assert";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import ForecastColumnType from "../component/ForecastPage/enum/ForecastColumnType";
import ForecastViewMode from "../component/ForecastPage/enum/ForecastViewMode";
import { ForecastViewState } from "../component/ForecastPage/types/ForecastViewState";
import formatDateAsColumnKey from "../component/ForecastPage/util/formatDateAsColumnKey";
import getClosingDateRangeCondition from "../component/ForecastPage/util/getClosingDateRangeCondition";
import getNextClosingDateFilterByModeAndColumns from "../component/ForecastPage/util/getNextClosingDateFilterByModeAndColumns";
import ViewMode from "../enum/ViewMode";
import { BoardViewState } from "../type/BoardViewState";

import {
  applyForecastViewSettings,
  applyKanbanViewSettings,
  applyListViewSettings,
  downloadDealData,
  exportDateDeals,
  exportStageDeals,
  fetchForecastDateDeals,
  fetchForecastDeals,
  fetchForecastNextPeriodDeals,
  fetchFunnelStagesSummary,
  fetchKanbanDeals,
  fetchKanbanStageDeals,
  fetchList,
  initializeForecastView,
  initializeKanbanView,
  initializeListView,
  setForecastHiddenColumnIds,
  setForecastViewMode,
  setKanbanHiddenColumnIds,
  switchView,
  updateDealView,
  updateForecastByDealChanging,
  updateKanbanByDealChanging,
} from "./actions";
import {
  getCurrentView,
  getForecastColumnCount,
  getForecastDeals,
  getForecastView,
  getForecastViewMode,
  getForecastViewState,
  getKanbanDeals,
  getKanbanView,
  getKanbanViewState,
  getListView,
  getListViewState,
  getUpdateViewStateCallback,
  getViewMode,
} from "./selectors";
import { NewBaseView } from "./types/BaseView";
import { DatesWithDeals } from "./types/ForecastView";
import { StagesWithDeals } from "./types/KanbanView";

const DEALS_PER_COLUMN = 25;

const OPEN_DEAL_STAGE_TYPE_FILTER = {
  [PlatformFilterOperator.NOT_CONTAINS]: [DealStageType.LOST, DealStageType.WON],
};

export const LIST_VIEW_KEY = "deal/listView";
export const KANBAN_VIEW_KEY = "deal/kanbanView";
export const FORECAST_VIEW_KEY = "deal/forecastView";

const reportGeneratedSuccessfullyMessage = defineMessage({
  id: "deal.list.exportRows.success",
  defaultMessage: "This export will be sent to your email shortly.",
  description: "Success message for sending csv file",
});

const getClosingDateFilter = (
  datesWithDeals: DatesWithDeals,
  viewMode: ForecastViewMode,
  date?: Date
): SimpleCondition => {
  const key = formatDateAsColumnKey(viewMode, date);

  let result: SimpleCondition = {
    fieldFilterName: DealFieldName.CLOSING_DATE,
    operator: FilterOperator.EMPTY,
    value: undefined,
  };
  if (date) {
    const columnType = datesWithDeals[key]?.type;
    const isPastClosingDate = columnType === ForecastColumnType.PAST_CLOSING_DATE;
    if (isPastClosingDate) {
      result = {
        fieldFilterName: DealFieldName.CLOSING_DATE,
        operator: FilterOperator.LESS_THAN,
        value: [
          (viewMode === ForecastViewMode.MONTH ? startOfMonth : startOfQuarter)(
            new Date()
          ).toISOString(),
        ],
      };
    } else {
      result = getClosingDateRangeCondition(viewMode, date);
    }
  }
  return result;
};

const excludeFunnelIdFromFilter = (
  filterModel: FilterModel,
  funnelId: Funnel["id"]
): FilterModel => {
  const funnelFilterIndex = getSimpleConditionIndexForField(filterModel, DealFieldName.FUNNEL);

  if (funnelFilterIndex < 0 || !isSimpleCondition(filterModel.conditions[funnelFilterIndex])) {
    return filterModel;
  }

  const condition = filterModel.conditions[funnelFilterIndex] as SimpleCondition;
  const { operator, value } = condition;

  if (operator === FilterOperator.EQUALS) {
    return value !== funnelId
      ? filterModel
      : removeSimpleConditionAtIndex(filterModel, funnelFilterIndex);
  } else if (operator === FilterOperator.IN_ANY) {
    return value.length === 1 && value[0] === funnelId
      ? removeSimpleConditionAtIndex(filterModel, funnelFilterIndex)
      : updateSimpleConditionAtIndex(filterModel, funnelFilterIndex, {
          ...condition,
          value: value.filter((id: Funnel["id"]) => id !== funnelId),
        });
  }

  return filterModel;
};

export function* onInitializeListView() {
  const savedFilterGetter: (entityType: EntityType) => SavedFilter | undefined =
    yield select(getSelectedFilter);
  const savedFilter = savedFilterGetter(EntityType.DEAL);
  if (!savedFilter) {
    yield put(
      initializeSavedFiltersForView({ entityType: EntityType.DEAL, viewKey: LIST_VIEW_KEY })
    );
  }
  yield put(initializeListView.success());
}
export function* onInitializeForecastView() {
  yield put(ensureStagesFetched.request(undefined));
  const stagesResult: ReturnType<
    typeof ensureStagesFetched.failure | typeof ensureStagesFetched.success
  > = yield take([ensureStagesFetched.success, ensureStagesFetched.failure]);

  if (isActionOf(ensureStagesFetched.failure, stagesResult)) {
    yield put(initializeForecastView.failure());
    return;
  }
  const savedFilterGetter: (entityType: EntityType) => SavedFilter | undefined =
    yield select(getSelectedFilter);
  const savedFilter = savedFilterGetter(EntityType.DEAL);
  if (!savedFilter) {
    yield put(
      initializeSavedFiltersForView({ entityType: EntityType.DEAL, viewKey: FORECAST_VIEW_KEY })
    );
  }

  yield put(initializeForecastView.success());
  // Now fetch an initial portion of deals. Not passing any viewState, because we don't need any changes there
  yield put(fetchForecastDeals.request({ viewState: {} }));
}

export function* onInitializeKanbanView() {
  const funnels: Funnel[] = yield select(getFunnels);

  if (!funnels.length) {
    yield put(initializeKanbanView.failure());
    return;
  }

  const viewState: ViewState = yield select(getKanbanViewState);

  const funnelId: Funnel["id"] = funnels[0].id;
  // if viewState doesn't have a funnel filter or if it is invalid, then filter by the 1st funnel
  const funnelCondition = getSimpleConditionForField(viewState.filter, DealFieldName.FUNNEL);
  if (!funnelCondition || funnelCondition.operator !== FilterOperator.EQUALS) {
    yield put(
      applyKanbanViewSettings({
        filter: appendFilterCondition(
          viewState.filter,
          createSimpleCondition(DealFieldName.FUNNEL, FilterOperator.EQUALS, funnelId)
        ),
      })
    );
  }

  yield put(ensureStagesFetched.request(undefined));
  const stagesResult: ReturnType<
    typeof ensureStagesFetched.failure | typeof ensureStagesFetched.success
  > = yield take([ensureStagesFetched.success, ensureStagesFetched.failure]);

  if (isActionOf(ensureStagesFetched.failure, stagesResult)) {
    yield put(initializeKanbanView.failure());
    return;
  }

  const savedFilterGetter: (entityType: EntityType) => SavedFilter | undefined =
    yield select(getSelectedFilter);
  const savedFilter = savedFilterGetter(EntityType.DEAL);
  if (!savedFilter) {
    yield put(
      initializeSavedFiltersForView({ entityType: EntityType.DEAL, viewKey: KANBAN_VIEW_KEY })
    );
  }
  yield put(initializeKanbanView.success());

  // Now fetch an initial portion of deals. Not passing any viewState, because we don't need any changes there
  yield put(fetchKanbanDeals.request({ viewState: {} }));
}

export function* onFetchList({ payload }: ReturnType<typeof fetchList.request>) {
  try {
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete payload.request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyListViewSettings(payload.request));
    }

    const listViewState: ViewState = yield select(getListViewState);

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(listViewState, LIST_VIEW_KEY);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : listViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : listViewState.range.endRow - listViewState.range.startRow;

    const organization: Organization = yield select(getOrganization);

    const [quickFilter, filter] = extractSimpleConditionForField(
      listViewState.filter,
      "quickFilter"
    );
    const $filters = {
      includeAccessStatus: true,
      ...convertToPlatformFilterModel(
        payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : filter,
        listViewState.columns,
        dealFieldModel
      ),
    };

    if (!payload.fetchOnlyWithoutFilters && quickFilter && isSimpleCondition(quickFilter)) {
      const { value } = quickFilter;
      if (!("$and" in $filters)) {
        $filters.$and = [];
      }
      $filters.$and!.push({
        $or: [
          { name: { $in: value } },
          { "account.name": { $in: value } },
          { "contact.name": { $in: value } },
        ],
      });
    }

    const requestPayload = {
      $aggs: { sum: ["amount", "adjustedAmount"] },
      $filters,
      $limit,
      $offset,
      $order: convertToPlatformSortModel(listViewState.sort),
    };
    const response: AggregatedListResponse<Deal> = yield callApi(
      "fetchDeals",
      organization.id,
      requestPayload
    );
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }
    yield put(
      fetchList.success({
        funnelSummary: {
          adjustedAmount: response.aggregations?.sum_adjustedAmount.value ?? 0,
          openAmount: response.aggregations?.sum_amount.value ?? 0,
        },
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback);
    }
    yield put(fetchList.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchFunnelStagesSummary({
  payload: funnelId,
}: ReturnType<typeof fetchFunnelStagesSummary.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const listViewState: ViewState = yield select(getListViewState);

    // We need to see all stages on the funnel chart
    const filter = removeSimpleConditionForFields(listViewState.filter, DealFieldName.STAGE);
    const payload = {
      $filters: {
        ...convertToPlatformFilterModel(filter, listViewState.columns, dealFieldModel),
      },
    };

    const response: ListResponse<StageSummary> = yield callApi(
      "fetchFunnelSummary",
      organization.id,
      funnelId,
      payload
    );

    yield put(fetchFunnelStagesSummary.success(response.data));
  } catch (error) {
    yield put(fetchFunnelStagesSummary.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchKanbanDeals({ payload }: ReturnType<typeof fetchKanbanDeals.request>) {
  try {
    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyKanbanViewSettings(payload.viewState));
    }

    const viewState: ViewState = yield select(getKanbanViewState);

    if (!payload.fetchOnlyWithoutFilters && !payload.updateOnly) {
      localSettings.setViewSettings(viewState, KANBAN_VIEW_KEY);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.viewState.range
        ? payload.viewState.range.startRow
        : viewState.range.startRow;

    const requestPayload = {
      $aggs: {
        innerHits: {
          limit: DEALS_PER_COLUMN,
        },
        primaryGroup: { field: "dealStageId" },
        sum: ["amount", "adjustedAmount"],
      },
      $filters: {
        includeAccessStatus: true,
        includeGroups: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : viewState.filter,
          viewState.columns,
          dealFieldModel
        ),
      },
      $limit: 0,
      $offset,
      $order: convertToPlatformSortModel(viewState.sort),
    };
    const organization: Organization = yield select(getOrganization);
    const response: AggregatedListResponse<Deal, ListResponseAggregation<Deal>[]> = yield callApi(
      "fetchDeals",
      organization.id,
      requestPayload
    );
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }

    const funnelStages: Record<Funnel["id"], Stage[]> = yield select(getFunnelStages);
    const funnelCondition = getSimpleConditionForField(viewState.filter, DealFieldName.FUNNEL);
    const funnelId =
      funnelCondition?.operator === FilterOperator.EQUALS
        ? funnelCondition.value
        : funnelCondition?.operator === FilterOperator.IN_ANY &&
            Array.isArray(funnelCondition.value) &&
            funnelCondition.value.length === 1
          ? funnelCondition.value[0]
          : undefined;

    let groupedDeals =
      response.aggregations.reduce<StagesWithDeals>(
        (result, { data, doc_count, key, sum_adjustedAmount, sum_amount }) => ({
          ...result,
          [key]: {
            adjustedAmount: sum_adjustedAmount?.value ?? 0,
            amount: sum_amount?.value ?? 0,
            deals: data ?? [],
            total: doc_count ?? 0,
          },
        }),
        {}
      ) ?? {};

    if (!funnelId) {
      // normally this should not happen, but it's still possible to get no funnel
      // in conditions when user just removed a filter rule
      // So we need to handle this gracefully
      yield put(
        fetchKanbanDeals.success({
          funnelSummary: {
            adjustedAmount:
              response.aggregations.reduce(
                (sum, { sum_adjustedAmount }) => sum + (sum_adjustedAmount?.value ?? 0),
                0
              ) ?? 0,
            openAmount:
              response.aggregations.reduce(
                (sum, { sum_amount }) => sum + (sum_amount?.value ?? 0),
                0
              ) ?? 0,
          },
          groupedDeals,
          totalFilteredRecords: response.total,
          totalRecords: response.accessible,
        })
      );
      return;
    }

    const closeStageIds = new Set(
      funnelStages[funnelId]
        .filter(({ type }) => [DealStageType.LOST, DealStageType.WON].includes(type))
        .map(({ id }) => id)
    );

    groupedDeals = {
      ...groupedDeals,
      ...funnelStages[funnelId]
        .filter(({ id }) => !(id.toString() in groupedDeals))
        .reduce(
          (result, { id }) => ({
            ...result,
            [id]: {
              adjustedAmount: 0,
              amount: 0,
              deals: [],
              total: 0,
            },
          }),
          {}
        ),
    };

    yield put(
      fetchKanbanDeals.success({
        funnelSummary: {
          adjustedAmount:
            response.aggregations
              .filter(({ key }) => !closeStageIds.has(key))
              .reduce((sum, { sum_adjustedAmount }) => sum + (sum_adjustedAmount?.value ?? 0), 0) ??
            0,
          openAmount:
            response.aggregations
              .filter(({ key }) => !closeStageIds.has(key))
              .reduce((sum, { sum_amount }) => sum + (sum_amount?.value ?? 0), 0) ?? 0,
        },
        groupedDeals,
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchKanbanDeals.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchKanbanStageDeals({
  payload: { callback, fetchAll, preventUpdate, stageId },
}: ReturnType<typeof fetchKanbanStageDeals.request>) {
  try {
    const viewState: ViewState = yield select(getKanbanViewState);
    const stagesWithDeals: StagesWithDeals = yield select(getKanbanDeals);
    const $offset = fetchAll ? 0 : (stagesWithDeals[stageId]?.deals.length ?? 0);

    if ($offset >= (stagesWithDeals[stageId]?.total ?? 0)) {
      yield put(
        fetchKanbanStageDeals.success({
          deals: [],
          preventUpdate,
          stageId,
          total: stagesWithDeals[stageId].total ?? 0,
        })
      );
      return;
    }

    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        includeGroups: true,
        ...convertToPlatformFilterModel(
          upsertSimpleConditions(
            viewState.filter,
            createSimpleCondition(DealFieldName.STAGE, FilterOperator.EQUALS, stageId)
          ),
          viewState.columns,
          dealFieldModel
        ),
      },
      $limit: fetchAll ? -1 : DEALS_PER_COLUMN,
      $offset,
      $order: convertToPlatformSortModel(viewState.sort),
    };
    const organization: Organization = yield select(getOrganization);
    const response: ListResponse<Deal> = yield callApi(
      "fetchDeals",
      organization.id,
      requestPayload
    );
    if (callback) {
      yield call(callback, response.data);
    }
    yield put(
      fetchKanbanStageDeals.success({
        deals: response.data,
        preventUpdate,
        stageId,
        total: response.total,
      })
    );
  } catch (error) {
    yield put(fetchKanbanStageDeals.failure({ stageId }));
    yield put(handleError({ error }));
  }
}

function* onSetKanbanHiddenColumnIds() {
  const viewState: BoardViewState = yield select(getKanbanViewState);
  localSettings.setViewSettings(viewState, KANBAN_VIEW_KEY);
}

export function* onUpdateKanbanByDealChanging({
  payload: deals,
}: ReturnType<typeof updateKanbanByDealChanging.request>) {
  try {
    const viewState: BoardViewState = yield select(getKanbanViewState);
    const funnelId: Funnel["id"] = getSimpleConditionForField(
      viewState.filter,
      DealFieldName.FUNNEL
    )!.value;
    const stagesWithDeals: StagesWithDeals = yield select(getKanbanDeals);
    const dealIds = new Set(deals.map(({ id }) => id));
    const stageIdsToUpdate = uniq(
      [
        // only take those deals whose funnel is not changed
        ...deals.filter(({ funnel }) => funnel.id === funnelId),
        ...Object.values(stagesWithDeals).reduce(
          (result, row) => [...result, ...row.deals.filter(({ id }) => dealIds.has(id))],
          [] as Deal[]
        ),
      ].map(({ stage }) => stage.id)
    );

    // Most probably we will have only 1 deal in action, but here we take maximum of touched stages
    const limit =
      (stageIdsToUpdate.length
        ? Math.max(...stageIdsToUpdate.map((key) => stagesWithDeals[key]?.deals.length ?? 0))
        : 0) + 1;

    const organization: Organization = yield select(getOrganization);
    let groupedDeals = {};

    const dealsTotalsPayload = {
      $aggs: {
        primaryGroup: { field: "dealStageId" },
        sum: ["amount", "adjustedAmount"],
      },
      $filters: {
        ...convertToPlatformFilterModel(viewState.filter, viewState.columns, dealFieldModel),
      },
      $limit: 0,
    };
    const requests = [callApi("fetchDeals", organization.id, dealsTotalsPayload)];

    if (stageIdsToUpdate.length) {
      const requestPayload = {
        $aggs: {
          innerHits: {
            limit: limit > MAX_AGGREGATION_LIMIT ? MAX_AGGREGATION_LIMIT : limit,
          },
          primaryGroup: { field: "dealStageId" },
          sum: ["amount", "adjustedAmount"],
        },
        $filters: {
          includeAccessStatus: true,
          includeGroups: true,
          ...convertToPlatformFilterModel(
            upsertSimpleConditions(
              viewState.filter,
              createSimpleCondition(DealFieldName.STAGE, FilterOperator.IN_ANY, stageIdsToUpdate)
            ),
            viewState.columns,
            dealFieldModel
          ),
        },
        $order: convertToPlatformSortModel(viewState.sort),
      };

      requests.push(callApi("fetchDeals", organization.id, requestPayload));
    }

    const [totalResponse, response]: [
      AggregatedListResponse<Deal, ListResponseAggregation<Deal>[]>,
      AggregatedListResponse<Deal, ListResponseAggregation<Deal>[]> | undefined,
    ] = yield all(requests);

    if (stageIdsToUpdate.length && response) {
      const incomingStageIds = new Set(response.aggregations.map(({ key }) => key));
      const emptyStageIds = stageIdsToUpdate.filter((stageId) => !incomingStageIds.has(stageId));

      groupedDeals = {
        ...response.aggregations.reduce<StagesWithDeals>(
          (stages, { data, doc_count, key, sum_adjustedAmount, sum_amount }) => ({
            ...stages,
            [key]: {
              adjustedAmount: sum_adjustedAmount.value,
              amount: sum_amount.value,
              deals: data ?? [],
              total: doc_count ?? 0,
            },
          }),
          {}
        ),
        ...emptyStageIds.reduce<StagesWithDeals>(
          (stages, stageId) => ({
            ...stages,
            [stageId]: {
              adjustedAmount: 0,
              amount: 0,
              deals: [],
              total: 0,
            },
          }),
          {}
        ),
      };
    }

    const funnelStages: Record<Funnel["id"], Stage[]> = yield select(getFunnelStages);
    const closeStageIds = new Set(
      funnelStages[funnelId]
        .filter(({ type }) => [DealStageType.LOST, DealStageType.WON].includes(type))
        .map(({ id }) => id)
    );

    yield put(
      updateKanbanByDealChanging.success({
        funnelSummary: {
          adjustedAmount:
            totalResponse.aggregations
              .filter(({ key }) => !closeStageIds.has(key))
              .reduce((sum, { sum_adjustedAmount }) => sum + sum_adjustedAmount.value, 0) ?? 0,
          openAmount:
            totalResponse.aggregations
              .filter(({ key }) => !closeStageIds.has(key))
              .reduce((sum, { sum_amount }) => sum + sum_amount.value, 0) ?? 0,
        },
        groupedDeals,
        totalFilteredRecords: totalResponse.total,
        totalRecords: totalResponse.accessible,
      })
    );
  } catch (error) {
    yield put(updateKanbanByDealChanging.failure(deals));
    yield put(handleError({ error }));
  }
}

export function* onFetchForecastDeals({ payload }: ReturnType<typeof fetchForecastDeals.request>) {
  try {
    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyForecastViewSettings(payload.viewState));
    }

    const viewState: ForecastViewState = yield select(getForecastViewState);

    if (!payload.fetchOnlyWithoutFilters && !payload.updateOnly) {
      localSettings.setViewSettings(viewState, FORECAST_VIEW_KEY);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.viewState.range
        ? payload.viewState.range.startRow
        : viewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.viewState.range
        ? payload.viewState.range.endRow - payload.viewState.range.startRow
        : viewState.range.endRow - viewState.range.startRow;

    const requestPayload = {
      $aggs: {
        innerHits: {
          limit: DEALS_PER_COLUMN,
        },
        primaryGroup: {
          field: "closingDate",
          interval: viewState.viewMode === ForecastViewMode.MONTH ? "month" : "quarter",
        },
        sum: ["amount", "adjustedAmount"],
      },
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : viewState.filter,
          viewState.columns,
          dealFieldModel
        ),
        "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(viewState.sort),
    };
    const organization: Organization = yield select(getOrganization);

    const dealsWithNoClosingDatesRequestPayload = {
      $aggs: {
        sum: ["amount", "adjustedAmount"],
      },
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters
            ? createEmptyFilterModel()
            : upsertSimpleConditions(
                viewState.filter,
                createSimpleCondition(DealFieldName.CLOSING_DATE, FilterOperator.EMPTY)
              ),
          viewState.columns,
          dealFieldModel
        ),
        "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
      },
      $limit: DEALS_PER_COLUMN,
      $order: convertToPlatformSortModel(viewState.sort),
    };

    const dealsWithOlderClosingDatesRequestPayload = {
      $aggs: {
        sum: ["amount", "adjustedAmount"],
      },
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters
            ? createEmptyFilterModel()
            : upsertSimpleConditions(
                viewState.filter,
                createSimpleCondition(DealFieldName.CLOSING_DATE, FilterOperator.LESS_THAN, [
                  (viewState.viewMode === ForecastViewMode.MONTH ? startOfMonth : startOfQuarter)(
                    new Date()
                  ).toISOString(),
                ])
              ),
          viewState.columns,
          dealFieldModel
        ),
        "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
      },
      $limit: DEALS_PER_COLUMN,
      $order: convertToPlatformSortModel(viewState.sort),
    };

    const dealsTotalsPayload = {
      $aggs: {
        sum: ["amount", "adjustedAmount"],
      },
      $filters: {
        ...convertToPlatformFilterModel(
          payload.fetchOnlyWithoutFilters
            ? createEmptyFilterModel()
            : removeSimpleConditionForFields(viewState.filter, DealFieldName.CLOSING_DATE),
          viewState.columns,
          dealFieldModel
        ),
        "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
      },
      $limit: 0,
    };

    const [response, noClosingDateResponse, totalResponse, olderClosingDateResponse]: [
      AggregatedListResponse<Deal, ListResponseAggregation<Deal>[]>,
      AggregatedListResponse<Deal>,
      AggregatedListResponse<Deal>,
      AggregatedListResponse<Deal>,
    ] = yield all([
      callApi("fetchDeals", organization.id, requestPayload),
      callApi("fetchDeals", organization.id, dealsWithNoClosingDatesRequestPayload),
      callApi("fetchDeals", organization.id, dealsTotalsPayload),
      callApi("fetchDeals", organization.id, dealsWithOlderClosingDatesRequestPayload),
    ]);

    payload.dataCallback?.(response);
    yield put(
      fetchForecastDeals.success({
        funnelSummary: {
          adjustedAmount: totalResponse.aggregations?.sum_adjustedAmount.value ?? 0,
          openAmount: totalResponse.aggregations?.sum_amount.value ?? 0,
        },
        groupedDeals: {
          ...(response.aggregations.reduce<DatesWithDeals>(
            (stages, { data, doc_count, key, sum_adjustedAmount, sum_amount }) => {
              const date = parseApiDateWithUtcTz(key);
              return {
                ...stages,
                [formatDateAsColumnKey(viewState.viewMode, date)]: {
                  adjustedAmount: sum_adjustedAmount.value,
                  amount: sum_amount.value,
                  date,
                  deals: data ?? [],
                  total: doc_count ?? 0,
                  type: ForecastColumnType.DATED,
                },
              };
            },
            {}
          ) ?? {}),
          [ForecastColumnType.NO_CLOSING_DATE]: {
            adjustedAmount: noClosingDateResponse.aggregations?.sum_adjustedAmount.value ?? 0,
            amount: noClosingDateResponse.aggregations?.sum_amount.value ?? 0,
            deals: noClosingDateResponse.data,
            total: noClosingDateResponse.total,
            type: ForecastColumnType.NO_CLOSING_DATE,
          },
          [ForecastColumnType.PAST_CLOSING_DATE]: {
            adjustedAmount: olderClosingDateResponse.aggregations?.sum_adjustedAmount.value ?? 0,
            amount: olderClosingDateResponse.aggregations?.sum_amount.value ?? 0,
            deals: olderClosingDateResponse.data,
            total: olderClosingDateResponse.total,
            type: ForecastColumnType.PAST_CLOSING_DATE,
          },
        },
        totalFilteredRecords: totalResponse.total,
        totalRecords: totalResponse.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchForecastDeals.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchForecastDateDeals({
  payload: { callback, date, fetchAll, preventUpdate },
}: ReturnType<typeof fetchForecastDateDeals.request>) {
  try {
    const viewState: ForecastViewState = yield select(getForecastViewState);
    const datesWithDeals: DatesWithDeals = yield select(getForecastDeals);
    const key = formatDateAsColumnKey(viewState.viewMode, date);
    const $offset = fetchAll ? 0 : (datesWithDeals[key]?.deals.length ?? 0);
    if ($offset === datesWithDeals[key]?.total) {
      yield put(
        fetchForecastDateDeals.success({
          date,
          deals: [],
          preventUpdate,
        })
      );
      return;
    }
    const closingDateFilter = getClosingDateFilter(datesWithDeals, viewState.viewMode, date);
    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          upsertSimpleConditions(viewState.filter, closingDateFilter),
          viewState.columns,
          dealFieldModel
        ),
        "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
      },
      $limit: fetchAll ? -1 : DEALS_PER_COLUMN,
      $offset,
      $order: convertToPlatformSortModel(viewState.sort),
    };
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<Deal> = yield callApi("fetchDeals", orgId, requestPayload);
    if (callback) {
      yield call(callback, response.data);
    }
    yield put(fetchForecastDateDeals.success({ date, deals: response.data, preventUpdate }));
  } catch (error) {
    yield put(fetchForecastDateDeals.failure({ date }));
    yield put(handleError({ error }));
  }
}

export function* onFetchForecastNextPeriodDeals() {
  try {
    const viewState: ForecastViewState = yield select(getForecastViewState);
    const columnCount: number = yield select(getForecastColumnCount);

    const currentClosingDateCondition: SimpleCondition = yield call(
      getSimpleConditionForField,
      viewState.filter,
      DealFieldName.CLOSING_DATE
    );

    const nextClosingDateCondition: SimpleCondition = yield call(
      getNextClosingDateFilterByModeAndColumns,
      viewState.viewMode,
      columnCount
    );

    yield put(
      applyForecastViewSettings({
        filter: upsertSimpleConditions(viewState.filter, {
          fieldFilterName: DealFieldName.CLOSING_DATE,
          operator: FilterOperator.IN_RANGE,
          value: [currentClosingDateCondition.value[0], nextClosingDateCondition.value[1]],
        }),
      })
    );

    const requestPayload = {
      $aggs: {
        innerHits: {
          limit: DEALS_PER_COLUMN,
        },
        primaryGroup: {
          field: "closingDate",
          interval: viewState.viewMode === ForecastViewMode.MONTH ? "month" : "quarter",
        },
        sum: ["amount", "adjustedAmount"],
      },
      $filters: {
        includeAccessStatus: true,
        ...convertToPlatformFilterModel(
          upsertSimpleConditions(viewState.filter, nextClosingDateCondition),
          viewState.columns,
          dealFieldModel
        ),
        "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
      },
      $order: convertToPlatformSortModel(viewState.sort),
    };

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: AggregatedListResponse<Deal, ListResponseAggregation<Deal>[]> = yield callApi(
      "fetchDeals",
      orgId,
      requestPayload
    );
    yield put(
      fetchForecastNextPeriodDeals.success({
        groupedDeals:
          response.aggregations.reduce<DatesWithDeals>(
            (stages, { data, doc_count, key, sum_adjustedAmount, sum_amount }) => {
              const date = parseApiDateWithUtcTz(key);
              return {
                ...stages,
                [formatDateAsColumnKey(viewState.viewMode, date)]: {
                  adjustedAmount: sum_adjustedAmount.value,
                  amount: sum_amount.value,
                  date,
                  deals: data ?? [],
                  total: doc_count ?? 0,
                  type: ForecastColumnType.DATED,
                },
              };
            },
            {}
          ) ?? {},
      })
    );
  } catch (error) {
    yield put(fetchForecastNextPeriodDeals.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateForecastByDealChanging({
  payload: deals,
}: ReturnType<typeof updateForecastByDealChanging.request>) {
  try {
    const viewState: ForecastViewState = yield select(getForecastViewState);
    const datesWithDeals: DatesWithDeals = yield select(getForecastDeals);
    const changedDealIds = new Set(deals.map(({ id }) => id));
    const keysToUpdate: Record<string, Date | undefined> = [
      ...Object.values(datesWithDeals)
        .reduce((result: Deal[], { deals }) => [...result, ...deals], [] as Deal[])
        .filter(({ id }) => changedDealIds.has(id)),
      ...deals,
    ].reduce(
      (keys, { closingDate }) => ({
        ...keys,
        [formatDateAsColumnKey(
          viewState.viewMode,
          closingDate ? parseApiDateWithUtcTz(closingDate) : undefined
        )]: closingDate ? parseApiDateWithUtcTz(closingDate) : undefined,
      }),
      {}
    );
    const datesWithDealsKeys = Object.keys(datesWithDeals);
    const keysToUpdateArray = Object.keys(keysToUpdate);
    const filteredPastKeys = keysToUpdateArray.filter((key) => !datesWithDealsKeys.includes(key));
    const keysToUpdateSet = new Set(Object.keys(keysToUpdate));
    const definedClosingKeysToUpdateSet = new Set(keysToUpdateSet);
    definedClosingKeysToUpdateSet.delete(ForecastColumnType.NO_CLOSING_DATE);
    // Most probably we will have only 1 created deal, but here we take maximum of touched periods
    const limit =
      (keysToUpdateSet.size
        ? Math.max(
            ...Object.keys(keysToUpdate).map((key) => datesWithDeals[key]?.deals.length ?? 0)
          )
        : 0) + 1;

    const organization: Organization = yield select(getOrganization);

    let groupedDeals: DatesWithDeals = {};

    if (definedClosingKeysToUpdateSet.size > 0) {
      const requestPayload = {
        $aggs: {
          innerHits: {
            limit: limit > MAX_AGGREGATION_LIMIT ? MAX_AGGREGATION_LIMIT : limit,
          },
          primaryGroup: {
            field: "closingDate",
            interval: viewState.viewMode === ForecastViewMode.MONTH ? "month" : "quarter",
          },
          sum: ["amount", "adjustedAmount"],
        },
        $filters: {
          includeAccessStatus: true,
          ...convertToPlatformFilterModel(
            appendFilterCondition(
              viewState.filter,
              createOrCondition(
                Array.from(definedClosingKeysToUpdateSet).map((key) =>
                  getClosingDateRangeCondition(viewState.viewMode, keysToUpdate[key]!)
                )
              )
            ),
            viewState.columns,
            dealFieldModel
          ),
          "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
        },
        $order: convertToPlatformSortModel(viewState.sort),
      };

      const response: AggregatedListResponse<Deal, ListResponseAggregation<Deal>[]> = yield callApi(
        "fetchDeals",
        organization.id,
        requestPayload
      );

      groupedDeals = {
        ...(response.aggregations.reduce<DatesWithDeals>(
          (result, { data, doc_count, key, sum_adjustedAmount, sum_amount }) => {
            const date = parseApiDateWithUtcTz(key);
            const newKey = formatDateAsColumnKey(viewState.viewMode, date);
            if (definedClosingKeysToUpdateSet.has(newKey)) {
              return {
                ...result,
                [newKey]: {
                  adjustedAmount: sum_adjustedAmount.value,
                  amount: sum_amount.value,
                  date,
                  deals: data ?? [],
                  total: doc_count ?? 0,
                  type: ForecastColumnType.DATED,
                },
              };
            }
            return result;
          },
          {}
        ) ?? {}),
      };
    }
    if (keysToUpdateSet.has(ForecastColumnType.NO_CLOSING_DATE)) {
      const dealsWithNoClosingDatesRequestPayload = {
        $aggs: {
          sum: ["amount", "adjustedAmount"],
        },
        $filters: {
          includeAccessStatus: true,
          ...convertToPlatformFilterModel(
            upsertSimpleConditions(
              viewState.filter,
              createSimpleCondition(DealFieldName.CLOSING_DATE, FilterOperator.EMPTY)
            ),
            viewState.columns,
            dealFieldModel
          ),
          "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
        },
        $limit: limit,
        $order: convertToPlatformSortModel(viewState.sort),
      };

      const noClosingDateResponse: AggregatedListResponse<Deal> = yield callApi(
        "fetchDeals",
        organization.id,
        dealsWithNoClosingDatesRequestPayload
      );
      groupedDeals = {
        ...groupedDeals,
        [ForecastColumnType.NO_CLOSING_DATE]: {
          adjustedAmount: noClosingDateResponse.aggregations?.sum_adjustedAmount.value ?? 0,
          amount: noClosingDateResponse.aggregations?.sum_amount.value ?? 0,
          deals: noClosingDateResponse.data,
          total: noClosingDateResponse.total,
          type: ForecastColumnType.NO_CLOSING_DATE,
        },
      };
    }

    if (
      filteredPastKeys.length > 0 ||
      keysToUpdateArray.includes(ForecastColumnType.PAST_CLOSING_DATE)
    ) {
      const dealsWithPastClosingDatesRequestPayload = {
        $aggs: {
          sum: ["amount", "adjustedAmount"],
        },
        $filters: {
          includeAccessStatus: true,
          ...convertToPlatformFilterModel(
            upsertSimpleConditions(
              viewState.filter,
              createSimpleCondition(
                DealFieldName.CLOSING_DATE,
                FilterOperator.LESS_THAN,
                (viewState.viewMode === ForecastViewMode.MONTH ? startOfMonth : startOfQuarter)(
                  new Date()
                )
              )
            ),
            viewState.columns,
            dealFieldModel
          ),
          "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
        },
        $limit: DEALS_PER_COLUMN,
        $order: convertToPlatformSortModel(viewState.sort),
      };

      const pastClosingDateResponse: AggregatedListResponse<Deal> = yield callApi(
        "fetchDeals",
        organization.id,
        dealsWithPastClosingDatesRequestPayload
      );
      groupedDeals = {
        ...groupedDeals,
        [ForecastColumnType.PAST_CLOSING_DATE]: {
          adjustedAmount: pastClosingDateResponse.aggregations?.sum_adjustedAmount.value ?? 0,
          amount: pastClosingDateResponse.aggregations?.sum_amount.value ?? 0,
          deals: pastClosingDateResponse.data,
          total: pastClosingDateResponse.total,
          type: ForecastColumnType.PAST_CLOSING_DATE,
        },
      };
    }

    const dealsTotalsPayload = {
      $aggs: {
        sum: ["amount", "adjustedAmount"],
      },
      $filters: {
        ...convertToPlatformFilterModel(
          removeSimpleConditionForFields(viewState.filter, DealFieldName.CLOSING_DATE),
          viewState.columns,
          dealFieldModel
        ),
        "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
      },
      $limit: 0,
    };

    const totalResponse: AggregatedListResponse<Deal> = yield callApi(
      "fetchDeals",
      organization.id,
      dealsTotalsPayload
    );

    yield put(
      updateForecastByDealChanging.success({
        funnelSummary: {
          adjustedAmount: totalResponse.aggregations?.sum_adjustedAmount.value ?? 0,
          openAmount: totalResponse.aggregations?.sum_amount.value ?? 0,
        },
        groupedDeals,
        totalFilteredRecords: totalResponse.total,
        totalRecords: totalResponse.accessible,
      })
    );
  } catch (error) {
    yield put(updateForecastByDealChanging.failure(deals));
    yield put(handleError({ error }));
  }
}

function* onSetForecastHiddenColumnIds() {
  const viewState: ForecastViewState = yield select(getForecastViewState);
  localSettings.setViewSettings(viewState, FORECAST_VIEW_KEY);
}

function* onSetForecastViewMode() {
  const viewMode: ForecastViewMode = yield select(getForecastViewMode);
  const { filter }: ForecastViewState = yield select(getForecastViewState);
  const updatedFilter = upsertSimpleConditions(
    filter,
    getNextClosingDateFilterByModeAndColumns(viewMode)
  );
  yield put(fetchForecastDeals.request({ viewState: { filter: updatedFilter } }));
}

export function* onFunnelDeleted({ payload: funnel }: ReturnType<typeof deleteFunnel.success>) {
  const kanbanViewState: BoardViewState = yield select(getKanbanViewState);
  const listViewState: BoardViewState = yield select(getListViewState);
  const forecastViewState: BoardViewState = yield select(getForecastViewState);
  const funnels: Funnel[] = yield select(getFunnels);
  const viewMode: ViewMode = yield select(getViewMode);

  const updatedListViewFilter = excludeFunnelIdFromFilter(listViewState.filter, funnel.id);
  if (listViewState.filter !== updatedListViewFilter) {
    yield put(applyListViewSettings({ filter: updatedListViewFilter }));
    if (viewMode === ViewMode.LIST) {
      yield put(fetchList.request({ request: {} }));
    }
  }

  const updatedForecastViewFilter = excludeFunnelIdFromFilter(forecastViewState.filter, funnel.id);
  if (forecastViewState.filter !== updatedForecastViewFilter) {
    yield put(applyForecastViewSettings({ filter: updatedForecastViewFilter }));
    if (viewMode === ViewMode.FORECAST) {
      yield put(fetchForecastDeals.request({ viewState: {} }));
    }
  }

  const funnelConditionIndex = getSimpleConditionIndexForField(
    kanbanViewState.filter,
    DealFieldName.FUNNEL
  );
  const funnelCondition =
    funnelConditionIndex < 0
      ? undefined
      : (kanbanViewState.filter.conditions[funnelConditionIndex] as SimpleCondition);
  if (funnelCondition && (funnelCondition.value as Funnel["id"]) === funnel.id) {
    yield put(
      applyKanbanViewSettings({
        filter: updateSimpleConditionAtIndex(
          kanbanViewState.filter,
          funnelConditionIndex,
          createSimpleCondition(
            DealFieldName.FUNNEL,
            FilterOperator.EQUALS,
            funnels.filter(({ id }) => id !== funnel.id)[0].id
          )
        ),
      })
    );
    if (viewMode === ViewMode.KANBAN) {
      yield put(fetchKanbanDeals.request({ viewState: {} }));
    }
  }
}

export function* onStagesChanged({
  payload: { funnelId },
}: ReturnType<typeof updateStages.success>) {
  const kanbanViewState: BoardViewState = yield select(getKanbanViewState);
  const listViewState: BoardViewState = yield select(getListViewState);
  const forecastViewState: BoardViewState = yield select(getForecastViewState);
  const viewMode: ViewMode = yield select(getViewMode);

  const updatedListViewFilter = excludeFunnelIdFromFilter(listViewState.filter, funnelId);
  if (listViewState.filter !== updatedListViewFilter && viewMode === ViewMode.LIST) {
    yield put(fetchList.request({ request: {} }));
  }

  const updatedForecastViewFilter = excludeFunnelIdFromFilter(forecastViewState.filter, funnelId);
  if (forecastViewState.filter !== updatedForecastViewFilter && viewMode === ViewMode.FORECAST) {
    yield put(fetchForecastDeals.request({ viewState: {} }));
  }

  const funnelCondition = getSimpleConditionForField(kanbanViewState.filter, DealFieldName.FUNNEL);
  if (
    funnelCondition &&
    (funnelCondition.value as Funnel["id"]) === funnelId &&
    viewMode === ViewMode.KANBAN
  ) {
    yield put(fetchKanbanDeals.request({ viewState: {} }));
  }
}

export function* onDownloadListData({
  payload: { columns, viewMode },
}: ReturnType<typeof downloadDealData>) {
  let { totalFilteredRecords, viewState }: NewBaseView = yield select(
    viewMode === ViewMode.LIST
      ? getListView
      : viewMode === ViewMode.KANBAN
        ? getKanbanView
        : getForecastView
  );
  let platformRequest = {};

  const closingDateCondition = getSimpleConditionForField(
    viewState.filter,
    DealFieldName.CLOSING_DATE
  );
  if (
    viewMode === ViewMode.FORECAST &&
    closingDateCondition &&
    Array.isArray(closingDateCondition.value)
  ) {
    // On forecast view, we need to remove closingDate from the view state filter to get all deals,
    // not only which have closing date. Instead, we use second maximum of a selected period
    // and put this to platform filter manually.
    const [closingDateCondition, filter] = extractSimpleConditionForField(
      viewState.filter,
      DealFieldName.CLOSING_DATE
    );
    viewState = { ...viewState, filter };
    platformRequest = {
      $filters: {
        $or: [
          {
            closingDate: {
              [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: closingDateCondition!.value[1],
            },
          },
          { closingDate: { [PlatformFilterOperator.EQUALS]: null } },
        ],
      },
    };
  }
  yield put(
    exportEntities.request({
      entityType: EntityType.DEAL,
      exportCreationSuccessMessage: i18nService
        .getIntl()
        ?.formatMessage(reportGeneratedSuccessfullyMessage),
      platformRequest,
      total: totalFilteredRecords,
      viewState: { ...viewState, columns },
    })
  );
}

export function* onSwitchView({ payload: { from, to } }: ReturnType<typeof switchView>) {
  const sourceView: NewBaseView = yield select(getCurrentView, from);
  const userFilter = getSimpleConditionForField(sourceView.viewState.filter, DealFieldName.USER);

  const funnelFilter = getSimpleConditionForField(
    sourceView.viewState.filter,
    DealFieldName.FUNNEL
  );

  // transfer selected funnels between views before switching
  // first of all, get currently selected funnels
  const destinationView: NewBaseView = yield select(getCurrentView, to);

  // then create an updated filter model where the funnel filter is either copied from the current view's filter
  // or is removed if the current view has no funnel filter
  let updatedFilterModel: FilterModel = destinationView.viewState.filter;
  if (funnelFilter) {
    updatedFilterModel = upsertSimpleConditions(updatedFilterModel, funnelFilter);
  } else {
    updatedFilterModel = removeSimpleConditionForFields(updatedFilterModel, DealFieldName.FUNNEL);
  }

  if (userFilter) {
    updatedFilterModel = upsertSimpleConditions(updatedFilterModel, userFilter);
  } else {
    updatedFilterModel = removeSimpleConditionForFields(updatedFilterModel, DealFieldName.USER);
  }

  switch (to) {
    case ViewMode.FORECAST:
    case ViewMode.LIST:
      {
        // the list view and forecast view only understand it when the funnel filter operator is IN_ANY,
        // so let's check if it is EQUALS (e.g. when switching from kanban view) and change
        const funnelCondition = getSimpleConditionForField(
          updatedFilterModel,
          DealFieldName.FUNNEL
        );
        if (funnelCondition?.operator === FilterOperator.EQUALS) {
          updatedFilterModel = upsertSimpleConditions(
            updatedFilterModel,
            createSimpleCondition(DealFieldName.FUNNEL, FilterOperator.IN_ANY, [
              funnelCondition.value,
            ])
          );
        }

        yield put(
          (to === ViewMode.LIST ? applyListViewSettings : applyForecastViewSettings)({
            filter: updatedFilterModel,
            selectedSavedFilterId: sourceView.viewState.selectedSavedFilterId,
          })
        );
      }
      break;
    case ViewMode.KANBAN:
      {
        // Kanban view requires funnel to be present in filters and requires exactly one funnel
        // So if the funnel filter is not present, we don't update kanban view's viewState and keep whatever
        // funnel was already selected there
        const funnelCondition = getSimpleConditionForField(
          updatedFilterModel,
          DealFieldName.FUNNEL
        );
        if (funnelCondition) {
          // if current funnel filter operator is IN_ANY, we take its funnel only when exactly one funnel is selected
          // also converting filter operator to EQUALS since that's the only operator kanban view understands
          if (
            funnelCondition.operator === FilterOperator.IN_ANY &&
            funnelCondition.value.length === 1
          ) {
            yield put(
              applyKanbanViewSettings({
                filter: upsertSimpleConditions(
                  updatedFilterModel,
                  createSimpleCondition(
                    DealFieldName.FUNNEL,
                    FilterOperator.EQUALS,
                    funnelCondition.value[0]
                  )
                ),
                selectedSavedFilterId: sourceView.viewState.selectedSavedFilterId,
              })
            );
          } else if (funnelCondition.operator === FilterOperator.EQUALS) {
            // when the current funnel filter operator is equals, we take it as-is
            yield put(
              applyKanbanViewSettings({
                filter: updatedFilterModel,
                selectedSavedFilterId: sourceView.viewState.selectedSavedFilterId,
              })
            );
          } else {
            yield put(
              applyKanbanViewSettings({
                selectedSavedFilterId: sourceView.viewState.selectedSavedFilterId,
              })
            );
          }
        } else {
          yield put(
            applyKanbanViewSettings({
              selectedSavedFilterId: sourceView.viewState.selectedSavedFilterId,
            })
          );
        }
      }
      break;
  }

  // then switch UI
  yield put(push(`${Path.DEAL}/${to}`));
}

export function* onUpdateDealView({ payload: { viewState } }: ReturnType<typeof updateDealView>) {
  const callback: (state: Partial<ViewState>) => undefined | void = yield select(
    getUpdateViewStateCallback
  );
  if (callback) {
    yield call(callback, viewState);
  }
}

export function* onExportStageDeals({
  payload: { columns, stageId },
}: ReturnType<typeof exportStageDeals>) {
  const { viewState }: NewBaseView = yield select(getKanbanView);
  const stagesWithDeals: StagesWithDeals = yield select(getKanbanDeals);

  yield put(
    exportEntities.request({
      entityType: EntityType.DEAL,
      total: stagesWithDeals[stageId]?.total,
      viewState: {
        ...viewState,
        columns,
        filter: upsertSimpleConditions(
          viewState.filter,
          createSimpleCondition(DealFieldName.STAGE, FilterOperator.EQUALS, stageId)
        ),
      },
    })
  );
}

export function* onExportDateDeals({
  payload: { columns, date },
}: ReturnType<typeof exportDateDeals>) {
  const viewState: ForecastViewState = yield select(getForecastViewState);
  const datesWithDeals: DatesWithDeals = yield select(getForecastDeals);
  const closingDateFilter = getClosingDateFilter(datesWithDeals, viewState.viewMode, date);
  const key = formatDateAsColumnKey(viewState.viewMode, date);

  yield put(
    exportEntities.request({
      entityType: EntityType.DEAL,
      platformRequest: {
        $filters: {
          "stage.type": OPEN_DEAL_STAGE_TYPE_FILTER,
        },
      },
      total: datesWithDeals[key]?.total,
      viewState: {
        ...viewState,
        columns,
        filter: upsertSimpleConditions(viewState.filter, closingDateFilter),
      },
    })
  );
}

function* onShowEntityView({ payload }: ReturnType<typeof showEntityView>) {
  const entityTypesWithExternalPreview: EntityType[] = yield select(
    getEntityTypesWithExternalPreview
  );
  if (payload.entityType && !entityTypesWithExternalPreview.includes(payload.entityType)) {
    return;
  }

  const { pathname }: RouterLocation<any> = yield select(getLocation);
  const isOnDealPage = pathname.startsWith(Path.DEAL);

  if (isOnDealPage) {
    const source: EntityViewSource = yield select(getEntityViewSource);
    const currentView = pathname.split("/")[2] as ViewMode;
    if (source === EntityViewSource.GLOBAL_SEARCH) {
      yield put(push(`${Path.DEAL}/${currentView}/${payload.entityId}`));
    }
  }
}

export function* dealSaga() {
  yield takeEvery(initializeListView.request, onInitializeListView);
  yield takeEvery(initializeKanbanView.request, onInitializeKanbanView);
  yield takeEvery(initializeForecastView.request, onInitializeForecastView);
  yield takeLatest(
    (action: Action) => isActionOf(fetchList.request)(action) && !action.payload.updateOnly,
    onFetchList
  );
  yield takeEvery(
    (action: Action) => isActionOf(fetchList.request)(action) && !!action.payload.updateOnly,
    onFetchList
  );
  yield takeLatest(
    (action: Action) => isActionOf(fetchKanbanDeals.request)(action) && !action.payload.updateOnly,
    onFetchKanbanDeals
  );
  yield takeEvery(
    (action: Action) => isActionOf(fetchKanbanDeals.request)(action) && !!action.payload.updateOnly,
    onFetchKanbanDeals
  );
  yield takeLatest(fetchKanbanStageDeals.request, onFetchKanbanStageDeals);
  yield takeEvery(updateKanbanByDealChanging.request, onUpdateKanbanByDealChanging);
  yield takeLatest(
    (action: Action) =>
      isActionOf(fetchForecastDeals.request)(action) && !action.payload.updateOnly,
    onFetchForecastDeals
  );
  yield takeEvery(
    (action: Action) =>
      isActionOf(fetchForecastDeals.request)(action) && !!action.payload.updateOnly,
    onFetchForecastDeals
  );
  yield takeLatest(fetchForecastDateDeals.request, onFetchForecastDateDeals);
  yield takeLatest(fetchForecastNextPeriodDeals.request, onFetchForecastNextPeriodDeals);
  yield takeEvery(updateForecastByDealChanging.request, onUpdateForecastByDealChanging);
  yield takeLatest(fetchFunnelStagesSummary.request, onFetchFunnelStagesSummary);
  yield takeEvery(downloadDealData, onDownloadListData);
  yield takeEvery(setForecastViewMode, onSetForecastViewMode);
  yield takeEvery(setForecastHiddenColumnIds, onSetForecastHiddenColumnIds);
  yield takeEvery(setKanbanHiddenColumnIds, onSetKanbanHiddenColumnIds);
  yield takeEvery(switchView, onSwitchView);
  yield takeEvery(deleteFunnel.success, onFunnelDeleted);
  yield takeEvery(updateStages.success, onStagesChanged);
  yield takeLatest(updateDealView, onUpdateDealView);
  yield takeLatest(exportStageDeals, onExportStageDeals);
  yield takeLatest(exportDateDeals, onExportDateDeals);
  yield takeLatest(showEntityView, onShowEntityView);
}
